import { useEffect, useRef, useState } from "react";
import { CookiesProvider } from "react-cookie";
import { loadableReady } from "@loadable/component";
import { hydrateRoot } from "react-dom/client";
import { ApolloProvider } from "@apollo/client/react";
import { BrowserRouter } from "react-router";
import TagManager from "react-gtm-module";
import { AppConfig, AppConfigProvider, useIsMounted } from "@ct-react/core";
import { defineLocale, LocaleProvider, useLocaleContext } from "@ct-react/locale";
import buildApolloClient from "@shared/graphql";
import { handleLocaleChange } from "@shared/locale";
import { APOLLO_CACHE_SCRIPT_ID, APP_CTX_SCRIPT_ID } from "@shared/constants";
import App from "./app/app";

const loadingLocale = defineLocale(navigator.languages || [], window.location.pathname);

const Wrapper = () => {

  const isMounted = useIsMounted();
  const { locale } = useLocaleContext();

  const ctx = useRef<AppConfig>(window.__APP_CTX__);
  const [ apollo, setApollo ] = useState(buildApolloClient(ctx.current, false, locale, window.__APOLLO_STATE__));

  useEffect(() => {
    if (!isMounted) return;
    setApollo(buildApolloClient(ctx.current, false, locale));
  }, [ locale ]);

  useEffect(() => {
    if (isMounted) return;
    delete (window as Partial<Window>).__APP_CTX__;
    const stateScriptTag = document.getElementById(APP_CTX_SCRIPT_ID);
    !!stateScriptTag && stateScriptTag.parentNode?.removeChild(stateScriptTag);
  }, [ ctx ]);

  useEffect(() => {
    if (isMounted) return;
    delete (window as Partial<Window>).__APOLLO_STATE__;
    const stateScriptTag = document.getElementById(APOLLO_CACHE_SCRIPT_ID);
    !!stateScriptTag && stateScriptTag.parentNode?.removeChild(stateScriptTag);
  }, [ apollo ]);

  useEffect(() => {
    ctx.current.env === "production" && TagManager.initialize({ gtmId: "GTM-MKRFFK6" });
  }, [ ctx ]);

  return (
    <CookiesProvider>
      <ApolloProvider client={apollo}>
        <AppConfigProvider value={ctx.current}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </AppConfigProvider>
      </ApolloProvider>
    </CookiesProvider>);

}

loadableReady()
  .then(() => navigator.serviceWorker.register("/public/sw.js"))
  .then(() => handleLocaleChange(loadingLocale))
  .then(() => hydrateRoot(
    document.getElementById("app")!,
    <LocaleProvider locale={loadingLocale}
                    beforeLocaleChange={locale => handleLocaleChange(locale)}>
      <Wrapper />
    </LocaleProvider>));
